// ── Owed and Upcoming Participation Pmts — participant scheduled payments ──
// Owed     = status 'Due' (past due, not paid).
// Upcoming = status 'Not Due' with a scheduled date within the next 30 days.
// Non Accrual = scheduled on or after the asset's non-accrual date (open
//   'Non Accrual' legal event on the loan or its account). Still listed — the
//   payment is contractual — but marked, and left out of the Dashboard and
//   Action Center counts, because we do not owe the participant interest on a
//   non-accrual asset. The section shows both totals.
import { useState, useEffect } from "react";
import { B } from "../../../shared/theme";
import { fmt, fmtDate } from "../../../shared/format";
import { Card, ErrorMsg, SectionHeader, Spinner, Btn, Icon, Badge, Th, ThNum, Td } from "../../../shared/ui";
import { useApp } from "../../../shared/appContext";

export const OwedPaymentsPage = () => {
  const { api, go, setModal, role, setConfirm, delRec } = useApp();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("getParticipantScheduledPmts")
      .then(d => { setRows(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(e => { setErr(e.message); setLoading(false); });
  };
  useEffect(() => { load(); }, []);

  if (loading) return <Spinner />;
  if (err) return <ErrorMsg msg={err} onRetry={load} />;

  const in30 = new Date(); in30.setHours(0, 0, 0, 0); in30.setDate(in30.getDate() + 30);
  const owed     = rows.filter(p => p.status === "Due");
  const upcoming = rows.filter(p => p.status === "Not Due" && p.scheduleddate && new Date(p.scheduleddate) <= in30);

  const numCell = { textAlign:"right", padding:"9px 12px", fontSize:13, borderBottom:"0.5px solid var(--color-border-tertiary)", fontVariantNumeric:"tabular-nums" };
  const hdrName = { padding:"7px 12px 6px", fontSize:11, fontWeight:600, color:"#116588", background:"#d4f1f9", borderTop:"0.5px solid #b8e6f5", borderBottom:"1.5px solid #b8e6f5", textTransform:"uppercase", letterSpacing:"0.06em" };
  const hdrNum  = { ...hdrName, textAlign:"right", fontVariantNumeric:"tabular-nums" };
  const totCell = { padding:"10px 12px", fontSize:13, fontWeight:600, borderTop:"1.5px solid var(--color-border-secondary)" };
  const totNum  = { ...totCell, textAlign:"right", fontVariantNumeric:"tabular-nums", color:"var(--color-text-primary)" };
  const sectionTitle = { fontSize:13, fontWeight:700, color:B.cyanDark, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:12, paddingBottom:6, borderBottom:"1.5px solid "+B.cyanLight };

  const openView = (p) => setModal({ type:"pspmt-view", rec:{ ...p, id:p.id, loanid:p.loanid } });
  const openEdit = (p) => setModal({ type:"pspmt-edit", rec:{ ...p, id:p.id, loanid:p.loanid }, onDone: load });

  const renderSection = (title, data, emptyMsg) => {
    const byAccount = data.reduce((acc, p) => { (acc[p.companyname] = acc[p.companyname] || []).push(p); return acc; }, {});
    const grand     = data.reduce((s, p) => s + (Number(p.totalscheduledpayment) || 0), 0);
    const grandRecv = data.reduce((s, p) => s + (Number(p.totalreceivedpmt) || 0), 0);
    const naRows    = data.filter(p => p.isnonaccrual);
    const counted   = data.filter(p => !p.isnonaccrual);
    const cntTotal  = counted.reduce((s, p) => s + (Number(p.totalscheduledpayment) || 0), 0);
    const cntRecv   = counted.reduce((s, p) => s + (Number(p.totalreceivedpmt) || 0), 0);
    return (
      <Card style={{ padding:16, overflowX:"auto", marginBottom:16 }}>
        <div style={sectionTitle}>
          {title}
          {naRows.length > 0 && (
            <span style={{ marginLeft:10, fontSize:11, fontWeight:500, textTransform:"none", letterSpacing:0,
                           color:"var(--color-text-secondary)" }}>
              {naRows.length} on non-accrual &mdash; listed, not counted in alerts
            </span>
          )}
        </div>
        {data.length === 0
          ? <div style={{ fontSize:13, color:"var(--color-text-secondary)" }}>{emptyMsg}</div>
          : <table style={{ width:"100%", borderCollapse:"collapse", whiteSpace:"nowrap" }}>
              <thead><tr>
                <Th>Loan #</Th>
                <Th>Due Date</Th>
                <ThNum>Principal</ThNum>
                <ThNum>Interest</ThNum>
                <ThNum>Fee</ThNum>
                <ThNum>Other</ThNum>
                <ThNum>Scheduled Amount</ThNum>
                <ThNum>Total Received</ThNum>
                <Th>Status</Th>
                <Th></Th>
              </tr></thead>
              <tbody>
                {Object.entries(byAccount).map(([accountName, pmts]) => {
                  const acctTotal = pmts.reduce((s, p) => s + (Number(p.totalscheduledpayment) || 0), 0);
                  const acctRecv  = pmts.reduce((s, p) => s + (Number(p.totalreceivedpmt) || 0), 0);
                  // Non-accrual start shown once per borrower, where it applies.
                  const naDates = [...new Set(pmts.filter(p => p.isnonaccrual).map(p => p.nonaccrualdate || ""))];
                  return [
                    <tr key={"hdr-" + accountName}>
                      <td colSpan={6} style={hdrName}>
                        {accountName}
                        {naDates.length > 0 && (
                          <span style={{ marginLeft:10, textTransform:"none", letterSpacing:0, fontWeight:600, color:"#993C1D" }}>
                            · Non Accrual{naDates[0] ? " since " + fmtDate(naDates[0]) : ""}
                          </span>
                        )}
                      </td>
                      <td style={hdrNum}>{fmt(acctTotal)}</td>
                      <td style={hdrNum}>{fmt(acctRecv)}</td>
                      <td style={hdrName}></td>
                      <td style={hdrName}></td>
                    </tr>,
                    ...pmts.map(p => (
                      <tr key={p.id}
                        onClick={() => openView(p)}
                        style={{ cursor:"pointer", opacity: p.isnonaccrual ? 0.72 : 1 }}
                        title={p.isnonaccrual ? "Scheduled on or after the non-accrual date — not counted as owed" : undefined}
                        onMouseEnter={e => e.currentTarget.style.background = "var(--color-background-secondary)"}
                        onMouseLeave={e => e.currentTarget.style.background = ""}>
                        <Td><span style={{ fontFamily:"var(--font-mono)", fontSize:12 }}>{p.loannumber}</span></Td>
                        <Td style={{ color:"var(--color-text-secondary)" }}>{fmtDate(p.scheduleddate)}</Td>
                        <td style={{ ...numCell, color:"var(--color-text-secondary)" }}>{fmt(p.scheduledprincipal || 0)}</td>
                        <td style={{ ...numCell, color:"var(--color-text-secondary)" }}>{fmt(p.scheduledinterest || 0)}</td>
                        <td style={{ ...numCell, color:"var(--color-text-secondary)" }}>{fmt(p.scheduledfee || 0)}</td>
                        <td style={{ ...numCell, color:"var(--color-text-secondary)" }}>{fmt(p.scheduledother || 0)}</td>
                        <td style={{ ...numCell, color:"var(--color-text-primary)", fontWeight:500 }}>{fmt(p.totalscheduledpayment)}</td>
                        <td style={{ ...numCell, color:"var(--color-text-primary)", fontWeight:500 }}>{fmt(p.totalreceivedpmt || 0)}</td>
                        <Td><div style={{ display:"flex", gap:4 }}>
                          <Badge s={p.status || "—"} />
                          {p.isnonaccrual ? <Badge s="Non Accrual" /> : null}
                        </div></Td>
                        <Td><div style={{ display:"flex", gap:6, justifyContent:"flex-end" }} onClick={e => e.stopPropagation()}>
                          <Btn onClick={() => openView(p)}><Icon type="view" /></Btn>
                          {role.canEdit && <Btn onClick={() => openEdit(p)}><Icon type="edit" /></Btn>}
                          {role.canDelete && <Btn variant="danger" onClick={() => setConfirm({ msg:"Delete this participant scheduled payment?", onConfirm:() => delRec("ParticipantScheduledPmts", p.id, "Deleted participant payment", load) })}><Icon type="delete" /></Btn>}
                        </div></Td>
                      </tr>
                    ))
                  ];
                })}
                <tr style={{ background:"var(--color-background-secondary)" }}>
                  <td colSpan={6} style={totCell}>Total</td>
                  <td style={totNum}>{fmt(grand)}</td>
                  <td style={totNum}>{fmt(grandRecv)}</td>
                  <td style={{ borderTop:"1.5px solid var(--color-border-secondary)" }}></td>
                  <td style={{ borderTop:"1.5px solid var(--color-border-secondary)" }}></td>
                </tr>
                {naRows.length > 0 && (
                  <tr style={{ background:"var(--color-background-secondary)" }}>
                    <td colSpan={6} style={{ ...totCell, borderTop:"none", paddingTop:2, fontWeight:500 }}>
                      Counted (excluding {naRows.length} non-accrual)
                    </td>
                    <td style={{ ...totNum, borderTop:"none", paddingTop:2 }}>{fmt(cntTotal)}</td>
                    <td style={{ ...totNum, borderTop:"none", paddingTop:2 }}>{fmt(cntRecv)}</td>
                    <td /><td />
                  </tr>
                )}
              </tbody>
            </table>}
      </Card>
    );
  };

  return <>
    <SectionHeader actions={[ <Btn key="back" onClick={() => go("dashboard")}>← Dashboard</Btn> ]} />
    {renderSection("Owed", owed, "No owed participant payments.")}
    {renderSection("Upcoming — next 30 days", upcoming, "No upcoming participant payments in the next 30 days.")}
  </>;
};
